import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from '@mui/material';

// Context
import { useStock } from '../contexts/StockContext';
import {
  Add as AddIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { productService } from '../services/productService';
import { supplierService } from '../services/supplierService';
import ProductForm from '../components/ProductForm';
import StockOrderSelector from '../components/StockOrderSelector';
import { ajouterAuPanier } from '../utils/panier';
import DialogueAjustementStock from '../components/produits/DialogueAjustementStock';
import DialogueDetailProduit from '../components/produits/DialogueDetailProduit';
import GalerieProduits from '../components/produits/GalerieProduits';
import DialogueConfirmation from '../components/commun/DialogueConfirmation';
import { stockEntryService } from '../services/stockEntryService';
import { stockExitService } from '../services/stockExitService';
import { alpha } from '@mui/material/styles';
import { PILE_CHASSE_FIXE } from '../theme/jetons';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, useMessageTraduit } from '../utils/messageTraduit';
import EnteteEcran from '../components/commun/EnteteEcran';
import BarreFiltres from '../components/commun/BarreFiltres';
import PiedTableau from '../components/commun/PiedTableau';
import { formatCurrency } from '../utils/currency';
import { canDelete, DELETABLE } from '../utils/permissions';

const CLE_VUE = 'produits-vue';

const Products = ({ showSnackbar, refreshUnreadNotifications, user, currentStock }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // 🔴 L'URL EST LA SOURCE DE VÉRITÉ DE L'OUVERTURE, pas un `useState`. C'est
  // ce qui fait que « précédent » ferme le dialogue : le paramètre disparaît,
  // donc `ouvert` devient faux — sans aucun code d'écoute de l'historique.
  const { id: produitDetailleId } = useParams();
  const { refreshTrigger, triggerRefresh } = useStock();
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useMessageTraduit();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /**
   * LISTE ou GALERIE — et le choix SURVIT au rechargement.
   *
   * ⚠️ DANS `localStorage`, PAS SUR LE COMPTE. Même nature que le mode
   * clair/sombre et la langue : c'est une préférence d'APPAREIL. Un opérateur
   * sur tablette dans l'entrepôt veut la galerie ; le même compte au bureau,
   * sur grand écran, veut le tableau. La mettre sur le compte imposerait le
   * choix de l'un à l'autre — et un aller-retour réseau avant de savoir quoi
   * peindre.
   * ⚠️ Lu À L'INITIALISATION, pas dans un `useEffect` : le lire après le
   * premier rendu ferait afficher le tableau puis basculer en galerie à chaque
   * chargement, exactement le clignotement que `ThemeModeContext` évite.
   */
  const [vue, setVue] = useState(() => {
    try {
      return localStorage.getItem(CLE_VUE) === 'galerie' ? 'galerie' : 'liste';
    } catch {
      // Sans stockage, la préférence vaut pour la session. Rien de plus.
      return 'liste';
    }
  });

  const changerVue = (nouvelle) => {
    if (!nouvelle) return;          // le groupe rend `null` si on reclique
    setVue(nouvelle);
    try { localStorage.setItem(CLE_VUE, nouvelle); } catch { /* sans effet */ }
  };
  // ⚠️ `total` est le nombre de RÉSULTATS, distinct du nombre de PAGES : c'est
  // ce que le client veut lire (« 47 produits »), et c'est aussi ce qui permet
  // de dire « aucun résultat » sans le confondre avec « une page vide ».
  const [total, setTotal] = useState(0);
  const [parPage, setParPage] = useState(20);

  /**
   * L'annonce avant un geste irréversible — plus de dialogue natif.
   *
   * 🔴 *Un dialogue natif se valide sans qu'on lise l'annonce* : il ne montre
   * rien, il bloque le fil, et il ne suit ni le thème ni la langue. Le
   * propriétaire a annulé une commande par accident sur exactement ce motif.
   */
  const [aConfirmer, setAConfirmer] = useState(null);
  const [confirmationEnCours, setConfirmationEnCours] = useState(false);

  const executerConfirmation = async () => {
    if (!aConfirmer?.executer) return;
    setConfirmationEnCours(true);
    try {
      await aConfirmer.executer();
    } finally {
      setConfirmationEnCours(false);
      setAConfirmer(null);
    }
  };

  const [orderSelectorOpen, setOrderSelectorOpen] = useState(false);
  // ⚠️ UN PRODUIT, PAS UN DICTIONNAIRE. L'état d'ajustement était indexé par
  // produit (`adjustments[id]`, `loadingAdjustment[id]`) parce que le
  // mini-formulaire vivait dans chaque ligne : toutes pouvaient être en cours
  // de modification. Le dialogue n'en ouvre qu'une — garder la forme
  // dictionnaire ferait survivre un écart saisi sur un produit après passage à
  // un autre, et ce serait invisible.
  const [produitAjuste, setProduitAjuste] = useState(null);
  const [ajustementEnCours, setAjustementEnCours] = useState(false);

  const { reset } = useForm();

  // Ajout d'un effet pour ouvrir le formulaire si prefillBarcode est présent
  const [prefillHandled, setPrefillHandled] = useState(false);
  const [lastPrefillBarcode, setLastPrefillBarcode] = useState(null);

  // Déclarer les fonctions de chargement avant les useEffect qui les utilisent
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        // 🔴 `parPage`, ET NON `20` EN DUR — LE SÉLECTEUR ÉTAIT INERTE.
        // Il écrivait bien `parPage`, et `parPage` figurait bien dans les
        // dépendances du `useCallback` : le changement DÉCLENCHAIT un
        // rechargement… qui repartait avec le même `per_page: 20`.
        // ⚠️ **CONSÉQUENCE MESURÉE, ET ELLE CACHE DES DONNÉES** : sur les 12
        // produits de la base, choisir « 10 par page » fait attendre DEUX
        // pages ; le serveur en renvoie 20, donc `pages = 1`, donc
        // `PiedTableau` masque la pagination — *elle ne s'affiche que si
        // `pages > 1`, ce qui est juste, mais elle recevait un `pages` faux*.
        // L'écran affiche « 12 éléments », « 10 par page », et aucun moyen
        // d'aller voir les deux qui manquent.
        // ⚠️ *Un réglage qui déclenche un rechargement sans changer la requête
        // est pire qu'un réglage mort : il donne la preuve visible qu'il a été
        // pris en compte.*
        per_page: parPage,
        search: searchTerm,
        supplier_id: selectedSupplier || undefined,
        low_stock_only: lowStockOnly,
      };
      
      const data = await productService.getProducts(params);
      setProducts(data.products);
      setTotalPages(data.pages);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(cle('messages.productLoadError'));
      showSnackbar(t('messages.productLoadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [page, parPage, searchTerm, selectedSupplier, lowStockOnly]);

  const loadSuppliers = useCallback(async () => {
    try {
      const data = await supplierService.getSuppliers();
      setSuppliers(data.suppliers);
    } catch (err) {
      console.error('Erreur lors du chargement des fournisseurs:', err);
    }
  }, []);

  React.useEffect(() => {
    if (
      location.state &&
      location.state.prefillBarcode &&
      (!prefillHandled || lastPrefillBarcode !== location.state.prefillBarcode)
    ) {
      setEditingProduct({ barcode: location.state.prefillBarcode });
      reset({ barcode: location.state.prefillBarcode });
      setDialogOpen(true);
      setPrefillHandled(true);
      setLastPrefillBarcode(location.state.prefillBarcode);
      // Nettoyer l'état pour éviter la réouverture au retour
      navigate(location.pathname, { replace: true, state: {} });
    }
    // On ne remet à false que si l'utilisateur ferme explicitement la fenêtre
    if (!dialogOpen && prefillHandled) {
      setPrefillHandled(false);
      setLastPrefillBarcode(null);
    }
  }, [location, reset, navigate, prefillHandled, lastPrefillBarcode, dialogOpen]);

  // Ajout d'un effet pour activer le filtre stock bas si demandé depuis le dashboard
  useEffect(() => {
    if (location.state && location.state.lowStockOnly) {
      setLowStockOnly(true);
      // Nettoyer l'état pour éviter la réactivation au retour
      navigate(location.pathname, { replace: true, state: {} });
      // Charger directement les produits filtrés
      setTimeout(() => { loadProducts(); }, 0);
    }
  }, [location, navigate, loadProducts]);

  // Effet pour charger les produits et les fournisseurs au montage et à chaque changement de filtre/page
  useEffect(() => {
    loadProducts();
    loadSuppliers();
  }, [loadProducts, loadSuppliers]);

  // Effet pour recharger les données quand le stock change
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadProducts();
      loadSuppliers();
    }
  }, [refreshTrigger, loadProducts, loadSuppliers]);

  const handleCreateProduct = async (productData) => {
    try {
      // Créer le produit d'abord
      const createdProduct = await productService.createProduct(productData);
      showSnackbar(t('messages.productAdded'), 'success');
      
      // Si une image a été sélectionnée, l'uploader
      if (productData.selectedImage) {
        try {
          await productService.uploadProductImage(createdProduct.id, productData.selectedImage);
          showSnackbar(t('messages.imageUploaded'), 'success');
        } catch (imageError) {
          console.error('Erreur lors de l\'upload de l\'image:', imageError);
          showSnackbar(t('messages.imageUploadError'), 'warning');
        }
      }
      
      setDialogOpen(false);
      reset();
      loadProducts();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.productCreateError'), 'error');
    }
  };

  const handleUpdateProduct = async (productData) => {
    try {
      await productService.updateProduct(editingProduct.id, productData);
      showSnackbar(t('messages.productUpdated'), 'success');
      setDialogOpen(false);
      setEditingProduct(null);
      reset();
      loadProducts();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.productUpdateError'), 'error');
    }
  };

  const demanderSuppressionProduit = (produit) => setAConfirmer({
    // 🔴 CE QUI EMPÊCHE LA SUPPRESSION EST DIT AVANT LE CLIC, pas après le
    // refus. Le serveur refuse un produit qui a des MOUVEMENTS de stock
    // (`stock_entries` ou `stock_exits`) — mesuré. *Annoncer la condition
    // évite un aller-retour dont l'utilisateur ne comprend le motif qu'à
    // l'échec.*
    consequences: t('products.deleteConsequence'),
    nom: produit?.name || '',
    executer: () => handleDeleteProduct(produit.id),
  });

  // ⚠️ LE BLOC ORPHELIN EST PARTI AVEC SA CONDITION. Ce corps était imbriqué
  // dans un `if (window.confirm(…))` retiré au bloquant 2′ ; les accolades ont
  // survécu à leur test. *Un bloc sans condition ne conditionne rien* — il ne
  // changeait pas le comportement, mais il donnait à lire une garde qui
  // n'existait plus.
  const handleDeleteProduct = async (productId) => {
    try {
      await productService.deleteProduct(productId);
      // ⚠️ Le formulaire peut être la SOURCE de la suppression depuis RU3-c :
      // le laisser ouvert sur un produit qui n'existe plus le ferait
      // enregistrer dans le vide au clic suivant.
      if (dialogOpen) handleCloseDialog();
      showSnackbar(t('messages.productDeleted'), 'success');
      loadProducts();
      triggerRefresh(); // Rafraîchir le dashboard
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('messages.productDeleteError'), 'error');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    reset(product);
    setDialogOpen(true);
  };

  const handleOpenDialog = () => {
    setEditingProduct(null);
    reset({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  // 🔴 CE CHARIOT ÉTAIT UN BOUTON MORT. Il naviguait vers `/purchase-orders`
  // avec un `state.prefillData` que PERSONNE ne lisait — un seul site dans tout
  // le dépôt, celui qui l'émet. *Un bouton qui ne fait rien est pire qu'un
  // bouton absent : il promet.*
  // ⚠️ ET IL PORTAIT UNE SIXIÈME ÉCRITURE DE LA QUANTITÉ DE RÉAPPRO —
  // `min_stock - current_stock`, SANS le `+1` : 2 au lieu de 3 sur un produit à
  // 1/3, donc un produit qui reste en alerte après réception. Elle n'a jamais
  // été constatée précisément parce que le bouton était mort. *Un code mort ne
  // se corrige pas : il se découvre faux le jour où on le ranime.*
  const handleOrderProduct = (product) => {
    if (!product.supplier_id) {
      showSnackbar(t('cart.noSupplier'), 'warning');
      return;
    }
    ajouterAuPanier(currentStock?.id, product);
    showSnackbar(t('cart.added', { produit: product.name }), 'success');
  };

  const handleOpenOrderSelector = () => {
    setOrderSelectorOpen(true);
  };

  const handleCloseOrderSelector = () => {
    setOrderSelectorOpen(false);
  };

  const handleOrdersCreated = (orders) => {
    showSnackbar(`${orders.length} ${t('messages.ordersCreated')}`, 'success');
    // Naviguer vers la page des commandes
    navigate('/purchase-orders');
  };

  const handleOuvrirAjustement = (product) => {
    setProduitAjuste(product);
  };

  // ⚠️ L'OPÉRATION EST REÇUE EN PARAMÈTRE, elle n'est plus lue dans un état
  // partagé : le dialogue est seul à la détenir, et la remonte au moment de
  // valider. Le lot RU3-b a remplacé l'écart SIGNÉ par un couple
  // (type, quantité) — un signe demandait au client de traduire son intention
  // en arithmétique, et surtout entrée et sortie n'écrivent pas la même chose.
  const handleValiderAjustement = async ({ type, quantite, motif }) => {
    if (!produitAjuste || !quantite) return;
    setAjustementEnCours(true);
    try {
      if (type === 'entree') {
        await stockEntryService.createStockEntry({
          product_id: produitAjuste.id,
          quantity: quantite,
          unit_price: produitAjuste.price,
          // ⚠️ COLONNES DIFFÉRENTES DES DEUX CÔTÉS, et ce n'est pas un caprice
          // de nommage : une entrée porte une RÉFÉRENCE (le bon de livraison),
          // une sortie un MOTIF. Le champ vide n'écrit rien plutôt qu'un
          // littéral inventé — « Ajustement via liste », qu'écrivait l'ancien
          // mini-formulaire, se lisait comme un motif choisi par quelqu'un.
          reference: motif,
        });
      } else {
        await stockExitService.createStockExit({
          product_id: produitAjuste.id,
          quantity: quantite,
          unit_price: produitAjuste.price,
          reason: motif,
        });
      }
      setProduitAjuste(null);
      showSnackbar(t('messages.stockAdjusted'), 'success');
      loadProducts();
      // 🔴 LE FORMULAIRE OUVERT DERRIÈRE LE DIALOGUE DEVIENT PÉRIMÉ. Depuis
      // RU3-c on ajuste SANS quitter la fiche produit : `loadProducts()`
      // rafraîchit le TABLEAU, pas l'objet `editingProduct` que le formulaire
      // tient. Sans cette relecture, on ajuste +10, on ferme le dialogue, et le
      // champ « Stock actuel » affiche encore l'ancien chiffre — alors que le
      // geste a réussi. Un écran qui contredit l'action qu'on vient de faire est
      // pire qu'un écran qui ne montre rien.
      if (editingProduct?.id === produitAjuste.id) {
        const frais = await productService.getProduct(produitAjuste.id);
        setEditingProduct(frais.product || frais);
      }
      if (refreshUnreadNotifications) {
        refreshUnreadNotifications();
      }
    } catch (e) {
      showSnackbar(t('messages.stockAdjustError'), 'error');
    } finally {
      setAjustementEnCours(false);
    }
  };

  if (loading && (!products || products.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  /**
   * 🔴 TOUT FILTRE REMET À LA PAGE 1, DANS LE MÊME GESTE QUE LE FILTRE.
   *
   * Sans ça, un lecteur en page 3 qui tape une recherche demande la page 3 d'un
   * résultat qui n'en a qu'une : **la liste revient vide, et rien ne dit
   * pourquoi**. Mesuré sur `PurchaseOrders` au lot P″ — effacer la recherche ne
   * débloquait même pas, il fallait cliquer la page 1 à la main.
   *
   * ⚠️ DANS LE GESTIONNAIRE, PAS DANS UN EFFET : un `useEffect` corrigerait
   * APRÈS coup, donc après une requête partie avec l'ancienne page. *Un état
   * dérivé d'un autre ne se corrige pas après coup, il s'écrit dans le même
   * geste.*
   */
  const filtrer = (appliquer) => (valeur) => {
    appliquer(valeur);
    setPage(1);
  };

  return (
    <Box sx={{ ml: 0, mr: 0, pl: 0, pr: 0 }}>
      <EnteteEcran titre={t('products.title')} compteur={total || undefined}>
        {/* ⚠️ LE BASCULEMENT EST DANS L'ENTÊTE, PAS DANS LA BARRE DE FILTRES.
            Un filtre change CE QU'ON VOIT ; celui-ci change COMMENT on le voit.
            Les mêmes produits, dans les deux vues — le mêler aux filtres
            laisserait croire qu'il en restreint la liste. */}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={vue}
          onChange={(_, v) => changerVue(v)}
        >
          <ToggleButton value="liste" aria-label={t('products.viewList')}>
            <Tooltip title={t('products.viewList')}><ViewListIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="galerie" aria-label={t('products.viewGallery')}>
            <Tooltip title={t('products.viewGallery')}><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
          {t('products.newProduct')}
        </Button>
      </EnteteEcran>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <BarreFiltres
        valeur={searchTerm}
        onChange={filtrer(setSearchTerm)}
        libelle={t('common.search')}
        actions={(
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ShoppingCartIcon />}
            onClick={handleOpenOrderSelector}
          >
            {t('products.order')}
          </Button>
        )}
      >
        {/* ⚠️ Les filtres restent ÉCRITS ICI : ils dépendent des données de
            l'écran — la liste des fournisseurs vient d'un appel. Un composant
            commun qui les fabriquerait devrait connaître ces données, et
            deviendrait le gabarit qu'on refuse. */}
        <TextField
          select
          size="small"
          label={t('products.supplier')}
          value={selectedSupplier}
          onChange={(e) => filtrer(setSelectedSupplier)(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('products.allSuppliers')}</MenuItem>
          {(suppliers || []).map((supplier) => (
            <MenuItem key={supplier.id} value={supplier.id}>
              {supplier.company_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('products.stock')}
          value={lowStockOnly ? 'low' : 'all'}
          onChange={(e) => filtrer(setLowStockOnly)(e.target.value === 'low')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">{t('products.allStocks')}</MenuItem>
          <MenuItem value="low">{t('products.lowStock')}</MenuItem>
        </TextField>
      </BarreFiltres>

      {/* 🔴 LES DEUX VUES PARTAGENT TOUT SAUF LEUR RENDU : mêmes filtres, même
          pagination, même source. Seule la façon de dessiner change — c'est ce
          qui garantit qu'elles ne peuvent pas montrer deux choses
          différentes. */}
      {vue === 'galerie' ? (
        <GalerieProduits
          produits={products}
          onAjuster={handleOuvrirAjustement}
          onCommander={handleOrderProduct}
          onModifier={handleEditProduct}
          /* ⚠️ L'OUVERTURE PASSE PAR L'URL, comme dans le tableau : le détail
             est une ROUTE (`/products/:id`), pas un `useState`. C'est ce qui
             rend « précédent » actif sans une ligne d'écoute de l'historique —
             décision du lot R, qu'une seconde mécanique d'ouverture aurait
             silencieusement défaite pour la galerie. */
          onOuvrir={(produit) => navigate(`/products/${produit.id}`)}
        />
      ) : (
      <TableContainer 
        component={Paper} 
        sx={{ 
          ml: 0, 
          mr: 0,
          '& .MuiPaper-root': {
            padding: 0,
            margin: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('products.name')}</TableCell>
              {/* ⚠️ DEUX COLONNES EN MOINS. Le CODE-BARRES passe sous le nom :
                  une colonne entière pour une donnée qu'on lit rarement poussait
                  montants et numéros sur deux lignes. Le STATUT est porté par la
                  LIGNE teintée — il ne consomme plus de colonne, et n'ajoute pas
                  une pastille de plus à un tableau qui en avait déjà. */}
              <TableCell>{t('products.supplier')}</TableCell>
              <TableCell align="center">{t('products.stock')}</TableCell>
              <TableCell align="right">{t('products.price')}</TableCell>
              <TableCell align="right">{t('products.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(products || []).map((product) => (
              /* 🔴 LA LIGNE ENTIÈRE PORTE L'ALERTE. Un badge « Stock bas » dans
                  une colonne se lit rangée par rangée ; une ligne teintée se voit
                  d'un coup d'œil sur tout le tableau — et libère la colonne qui
                  poussait montants et numéros sur deux lignes.
                  ⚠️ LA COULEUR N'EST PAS SEULE PORTEUSE : le CHIFFRE du stock est
                  teinté lui aussi, et reste lisible pour qui ne distingue pas les
                  teintes. Même règle que pour les pastilles du lot RU2.
                  ⚠️ UNE TEINTE TIRÉE DE LA PALETTE AVEC UNE OPACITÉ, jamais une
                  couleur claire en dur : posée sur le fond du mode courant, elle
                  reste lisible en clair comme en sombre. Une teinte figée aurait
                  rejoué le défaut de RU1. */
              <TableRow
                key={product.id}
                hover
                style={{ cursor: 'pointer' }}
                sx={product.is_low_stock ? {
                  // ⚠️ `alpha()` ET NON UNE CONCATÉNATION HEXA. Coller '22' à
                  // `palette.error.main` marche tant que la palette rend un
                  // `#rrggbb` — mesuré, c'est le cas aujourd'hui. Le jour où une
                  // palette rendrait `rgb(…)`, la chaîne obtenue serait une
                  // couleur INVALIDE : le navigateur l'ignore, la ligne cesse
                  // d'être teintée, et RIEN ne le signale.
                  //
                  // 🔴 EN MODE CLAIR, LES DEUX TEINTES SONT DÉSACCORDÉES — pas
                  // seulement foncées. À opacité COMMUNE, elles ne peuvent pas
                  // se distinguer : `warning.main` et `error.main` ne diffèrent
                  // que de **13,5 points de L\*** à la source, donc les deux
                  // lignes diffèrent de 13,5 × α. À 0,08 cela faisait **1,4** —
                  // sous le seuil de perception (≈ 2,3). Monter l'opacité
                  // commune assez haut pour les séparer aurait fait passer le
                  // texte secondaire sous le seuil de lisibilité. Il faut donc
                  // DEUX opacités, et c'est aussi le bon sens du domaine :
                  // épuisé est plus grave que bas, la ligne doit peser plus.
                  //
                  // Mesuré, en écart perceptif ΔE contre le repère du dépôt
                  // (le séparateur `#e2e8f0` se détache du blanc de ΔE 9,4) :
                  //   basse ↔ fond    11,0
                  //   basse ↔ épuisée 13,9
                  //   épuisée ↔ fond  20,5
                  // et le texte secondaire tient : 6,58 et 5,22 pour un seuil
                  // de 4,5 — à condition du jeton corrigé dans `creerTheme.js`.
                  //
                  // ⚠️ LE MODE SOMBRE N'EST PAS TOUCHÉ. Il est juste tel quel, et
                  // l'aligner « pour la symétrie » casserait ce qui marche : sur
                  // un fond sombre les deux teintes se séparent déjà, parce que
                  // l'écart se lit contre du noir et non contre du blanc.
                  backgroundColor: (theme) => {
                    const epuise = product.current_stock === 0;
                    const opacite = theme.palette.mode === 'dark'
                      ? 0.13
                      : (epuise ? 0.24 : 0.13);
                    return alpha(theme.palette[epuise ? 'error' : 'warning'].main, opacite);
                  },
                } : undefined}
                onClick={e => {
                /* 🔴 LA LIGNE MÈNE AU DÉTAIL, LE CRAYON GARDE LE FORMULAIRE.
                   Jusqu'ici les deux faisaient la MÊME chose : la ligne était
                   un doublon du crayon, donc rien n'est perdu à la
                   ré-orienter. Lire est le geste fréquent, modifier le geste
                   rare — la grande cible sert le geste fréquent, la petite
                   cible sert le geste rare.

                   ⚠️ LA GARDE `closest('button')` COUVRE LES QUATRE BOUTONS,
                   et elle remonte depuis la cible RÉELLE : un clic sur le
                   `<svg>` d'une icône, ou sur son `<path>`, remonte jusqu'au
                   `<button>` de MUI. Sans elle, commander ou supprimer
                   ouvrirait aussi le détail. */
                if (e.target.closest('button')) return;
                navigate(`/products/${product.id}`);
              }}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {product.name}
                    </Typography>
                    {/* ⚠️ CHASSE FIXE, ET CE N'EST PAS DÉCORATIF : un code-barres
                        se compare CHIFFRE À CHIFFRE. En chasse proportionnelle,
                        deux codes qui ne diffèrent que d'un caractère ont l'air
                        identiques ; alignés, l'écart saute aux yeux — et on peut
                        le lire à voix haute sans se perdre.
                        ⚠️ LA DESCRIPTION DISPARAÎT D'ICI : elle ajoutait une
                        seconde ligne à CHAQUE rangée, pour un texte qu'on ne lit
                        pas en balayant une liste. Elle reste dans la fiche
                        produit, là où on la cherche. */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: PILE_CHASSE_FIXE, display: 'block' }}
                    >
                      {product.barcode}
                    </Typography>
                  </TableCell>
                <TableCell>
                  {product.supplier_name || t('products.noSupplier')}
                </TableCell>
                <TableCell align="center">
                  {/* 🔴 LE MINI-FORMULAIRE QUITTE LA CELLULE. Il y avait ici
                      « − 0 + Valider » sur CHAQUE ligne : quatre commandes par
                      rangée, soit des dizaines de cibles cliquables dans un
                      tableau qu'on parcourt surtout pour LIRE. Et son bouton
                      « Valider » était violet sur violet, illisible — il ne se
                      corrige pas, il DISPARAÎT avec ce qui l'entourait.
                      ⚠️ L'ajustement ne se perd pas : il devient un dialogue
                      (lot RU3-b), où il y a la place de montrer ce que
                      l'opération donnera AVANT de la valider. */}
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      color: !product.is_low_stock ? 'text.primary'
                        : product.current_stock === 0 ? 'error.main'
                          : 'warning.main',
                    }}
                  >
                    {product.current_stock}
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      {' / '}{product.min_stock}
                    </Box>
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => { e.stopPropagation(); handleOuvrirAjustement(product); }}
                    sx={{ mt: 0.5 }}
                  >
                    {t('products.adjust')}
                  </Button>
                </TableCell>
                {/* ⚠️ `align="right"` DES DEUX CÔTÉS. L'en-tête l'avait déjà, la
                    cellule non : le titre « Prix » se serait aligné à droite
                    au-dessus de montants alignés à gauche. Un désaccord
                    en-tête/cellule ne casse rien — il rend juste la colonne
                    illisible, et c'est pour ça qu'il survit. */}
                <TableCell align="right">
                  {product.price != null ? formatCurrency(product.price, product.currency || 'EUR') : '-'}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOrderProduct(product)}
                    title={t('products.orderThisProduct')}
                  >
                    <ShoppingCartIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEditProduct(product)}
                  >
                    <EditIcon />
                  </IconButton>
                  {canDelete(user, DELETABLE.PRODUCTS) && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => demanderSuppressionProduit(product)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <DialogueConfirmation
        ouvert={Boolean(aConfirmer)}
        titre={t('products.deleteTitle')}
        question={t('products.deleteQuestion', { nom: aConfirmer?.nom || '' })}
        consequences={aConfirmer?.consequences}
        libelleConfirmer={t('common.delete')}
        enCours={confirmationEnCours}
        onConfirmer={executerConfirmation}
        onFermer={() => setAConfirmer(null)}
      />

      <PiedTableau
        total={total}
        page={page}
        pages={totalPages}
        parPage={parPage}
        onPage={setPage}
        onParPage={(n) => { setParPage(n); setPage(1); }}
        libelleTotal={`${total} ${t('products.title').toLowerCase()}`}
        libelleParPage={t('common.perPage')}
      />

      {/* Dialog pour créer/modifier un produit */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? (editingProduct.name || t('products.title')) : t('products.newProduct')}
        </DialogTitle>
        <DialogContent>
          <ProductForm
            product={editingProduct}
            suppliers={suppliers}
            onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
            onCancel={handleCloseDialog}
            loading={loading}
            onAjuster={editingProduct ? () => handleOuvrirAjustement(editingProduct) : null}
            /* ⚠️ LE DROIT DE SUPPRIMER SE DÉCIDE ICI, comme pour l'icône de la
               ligne : le formulaire ne connaît pas les rôles et ne doit pas les
               apprendre. `null` fait DISPARAÎTRE le bouton — le composant n'a
               aucun état « grisé » à gérer. */
            onSupprimer={
              editingProduct && canDelete(user, DELETABLE.PRODUCTS)
                ? () => demanderSuppressionProduit(editingProduct)
                : null
            }
          />
        </DialogContent>
      </Dialog>

      {/* Sélecteur de commandes depuis le stock */}
      <StockOrderSelector
        open={orderSelectorOpen}
        onClose={handleCloseOrderSelector}
        onOrdersCreated={handleOrdersCreated}
      />

      {/* 🔴 LE DÉTAIL, OUVERT PAR L'URL. Il est monté en permanence mais ne
          charge rien tant que `ouvert` est faux — sinon la liste ferait trois
          requêtes au montage pour un dialogue que personne ne regarde.
          ⚠️ `key` SUR L'IDENTIFIANT : passer d'un produit à l'autre sans fermer
          (c'est possible en changeant l'URL) doit REMONTER le composant, sinon
          l'onglet, la page et l'ancien produit survivraient à la bascule — la
          même péremption d'état que le formulaire vient de nous coûter. */}
      <DialogueDetailProduit
        key={produitDetailleId}
        id={produitDetailleId}
        ouvert={Boolean(produitDetailleId)}
        onFermer={() => navigate('/products')}
        onModifier={async () => {
          // Le détail SE FERME et le formulaire s'ouvre — on n'empile pas deux
          // dialogues, le retour deviendrait ambigu. Le produit est RELU : le
          // détail a pu l'ajuster, et le tableau derrière peut être périmé.
          navigate('/products');
          try {
            const frais = await productService.getProduct(produitDetailleId);
            setEditingProduct(frais.product || frais);
            setDialogOpen(true);
            loadProducts();
          } catch (e) {
            showSnackbar(t('products.notFound'), 'error');
          }
        }}
        showSnackbar={showSnackbar}
        currentStock={currentStock}
        refreshUnreadNotifications={refreshUnreadNotifications}
      />

      <DialogueAjustementStock
        produit={produitAjuste}
        ouvert={Boolean(produitAjuste)}
        enCours={ajustementEnCours}
        onFermer={() => setProduitAjuste(null)}
        onValider={handleValiderAjustement}
      />
    </Box>
  );
};

export default Products; 
